/**
 * MFA Code Input Component
 * 6-digit verification code input with auto-submit
 * SEC-011: MFA Implementation
 */

import { useState, useRef, useEffect, KeyboardEvent } from 'react';
import { useLanguage } from '@/contexts/language';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface MFACodeInputProps {
  onComplete: (code: string) => void;
  isVerifying?: boolean;
  error?: string | null;
  autoFocus?: boolean;
}

export function MFACodeInput({
  onComplete,
  isVerifying = false,
  error = null,
  autoFocus = true,
}: MFACodeInputProps) {
  const { t } = useLanguage();
  const [code, setCode] = useState<string[]>(Array(6).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [autoFocus]);

  useEffect(() => {
    // Clear on error
    if (error) {
      setCode(Array(6).fill(''));
      inputRefs.current[0]?.focus();
    }
  }, [error]);

  const handleChange = (index: number, value: string) => {
    // Only allow digits
    const digit = value.replace(/\D/g, '').slice(-1);

    if (digit || value === '') {
      const newCode = [...code];
      newCode[index] = digit;
      setCode(newCode);

      // Auto-focus next input
      if (digit && index < 5) {
        inputRefs.current[index + 1]?.focus();
      }

      // Auto-submit when complete
      if (newCode.every(d => d !== '') && newCode.length === 6) {
        onComplete(newCode.join(''));
      }
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (code[index] === '' && index > 0) {
        // Move to previous input on backspace if current is empty
        inputRefs.current[index - 1]?.focus();
      } else {
        // Clear current input
        const newCode = [...code];
        newCode[index] = '';
        setCode(newCode);
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);

    if (pastedData.length === 6) {
      const newCode = pastedData.split('');
      setCode(newCode);
      inputRefs.current[5]?.focus();
      onComplete(pastedData);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex justify-center gap-2">
        {code.map((digit, index) => (
          <Input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            disabled={isVerifying}
            className={cn(
              'w-12 h-14 text-center text-2xl font-bold',
              error && 'border-red-500',
              isVerifying && 'opacity-50 cursor-not-allowed'
            )}
            aria-label={`Digit ${index + 1}`}
          />
        ))}
      </div>

      {error && (
        <p className="text-sm text-red-500 text-center">{error}</p>
      )}

      {isVerifying && (
        <p className="text-sm text-muted-foreground text-center">{t('mfa.codeInput.verifying')}</p>
      )}
    </div>
  );
}
